import React, { useEffect, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import api from '../Api';

function LaporanContactCenter() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [channelSummary, setChannelSummary] = useState([]);

  const [wilayahList, setWilayahList] = useState([]);
  const [selectedWilayah, setSelectedWilayah] = useState('');

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      setUserRole(userData.role_id);
    }

    fetchWilayah();
    fetchData();
  }, []);

  // ================= FETCH WILAYAH =================
  const fetchWilayah = async () => {
    try {
      const response = await api.get('/wilayah');
      setWilayahList(response.data || []);
    } catch (error) {
      console.error('Gagal mengambil wilayah:', error);
    }
  };

  // ================= FETCH DATA =================
  const fetchData = async (customFrom = '', customTo = '', wilayahId = '') => {
    try {
      setLoading(true);

      const params = {};

      if (customFrom && customTo) {
        params.from = customFrom;
        params.to = customTo;
      }

      if (wilayahId) {
        params.wilayah_id = wilayahId;
      }

      const response = await api.get('/report/bsd-json', { params });

      setData(response.data.data);
      setSummary(response.data.summary);
      setChannelSummary(response.data.channel_summary || []);
    } catch (error) {
      console.error('Gagal mengambil data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWilayah();
    fetchData();
  }, []);

  // ================= FILTER =================
  const handleFilter = () => {
    fetchData(from, to, selectedWilayah);
  };

  const handleReset = () => {
    setFrom('');
    setTo('');
    setSelectedWilayah('');
    fetchData();
  };

  // ================= EXPORT EXCEL =================
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);

      const params = {};

      if (from && to) {
        params.from = from;
        params.to = to;
      }

      if (selectedWilayah) {
        params.wilayah_id = selectedWilayah;
      }

      const response = await api.get('/report/bsd', {
        params,
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorJson = JSON.parse(text);
        alert(errorJson.message || 'Terjadi kesalahan saat export');
        return;
      }

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'laporan_contactcenter.xlsx';

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Gagal export excel:', error);
      alert('Gagal export laporan');
    } finally {
      setExportLoading(false);
    }
  };

  // ================= FORMAT DATE =================
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('id-ID');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';

    const value = String(phone).trim();

    // +62812... -> 0812...
    if (value.startsWith('+62')) {
      return '0' + value.slice(3);
    }

    // 62812... -> 0812...
    if (value.startsWith('62')) {
      return '0' + value.slice(2);
    }

    return value;
  };

  // ================= TABLE COLUMNS =================
  // const columns = useMemo(
  //   () => [
  //     { name: 'No', selector: (_, i) => i + 1, width: '60px' },
  //     { name: 'Ticket ID', selector: (row) => row.ticket_id, sortable: true },
  //     { name: 'Channel', selector: (row) => row.channel },
  //     { name: 'Input By', selector: (row) => row.input_by },
  //     { name: 'Category', selector: (row) => row.category },
  //     { name: 'Wilayah', selector: (row) => row.wilayah },
  //     { name: 'No. Telp', selector: (row) => row.no_telp },
  //     {
  //       name: 'Input Time',
  //       selector: (row) => formatDate(row.created_at),
  //     },
  //     {
  //       name: 'Closed Time',
  //       selector: (row) => formatDate(row.closed_at),
  //     },
  //     {
  //       name: 'Duration (Min)',
  //       selector: (row) => Number(row.duration_minutes).toFixed(2),
  //       right: true,
  //     },
  //     {
  //       name: 'Status',
  //       cell: (row) => (
  //         <span className={`badge ${row.status === 'Closed' ? 'bg-success' : 'bg-danger'}`}>{row.status}</span>
  //       ),
  //     },
  //   ],
  //   [],
  // );

  const columns = useMemo(
    () => [
      {
        name: 'No',
        selector: (_, i) => i + 1,
        width: '70px',
        center: true,
      },

      {
        name: 'Ticket ID',
        selector: (row) => row.ticket_id,
        sortable: true,
        width: '190px',
        wrap: true,
      },

      {
        name: 'Channel',
        selector: (row) => row.channel,
        width: '150px',
        wrap: true,
      },

      {
        name: 'Input By',
        selector: (row) => row.input_by,
        width: '170px',
        wrap: true,
      },

      {
        name: 'Category',
        selector: (row) => row.category,
        width: '250px',
        wrap: true,
      },

      {
        name: 'Wilayah',
        selector: (row) => row.wilayah,
        width: '200px',
        wrap: true,
      },

      {
        name: 'No. Telp',
        selector: (row) => formatPhone(row.no_telp),
        width: '170px',
        wrap: true,
      },

      {
        name: 'Input Time',
        selector: (row) => formatDate(row.created_at),
        width: '190px',
        wrap: true,
      },

      {
        name: 'Closed Time',
        selector: (row) => (row.closed_at ? formatDate(row.closed_at) : '-'),
        width: '190px',
        wrap: true,
      },

      {
        name: 'Duration (Min)',
        selector: (row) =>
          row.duration_minutes !== null && row.duration_minutes !== undefined
            ? Number(row.duration_minutes).toFixed(2)
            : '-',
        width: '160px',
        right: true,
      },

      {
        name: 'Status',
        width: '150px',
        center: true,
        cell: (row) => (
          <span className={`badge ${row.status === 'Closed' ? 'bg-success' : 'bg-danger'}`}>{row.status}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="container-fluid py-4">
      {/* ===== HEADER ===== */}
      <div className="mb-4">
        <div className="mb-3">
          <h3 className="fw-bold text-dark mb-1">Laporan Contact Center</h3>
          <p className="text-muted mb-0">Branch Performance Report</p>
        </div>

        <div className="d-flex gap-2 align-items-end flex-wrap">
          {/* ===== WILAYAH FILTER ===== */}
          {userRole !== 2 && (
            <div className="col-md-3">
              <label className="form-label">Wilayah</label>
              <select
                className="form-select"
                value={selectedWilayah}
                onChange={(e) => setSelectedWilayah(e.target.value)}
              >
                <option value="">Semua Wilayah</option>
                {wilayahList.map((wilayah) => (
                  <option key={wilayah.kd_wilayah} value={wilayah.kd_wilayah}>
                    {wilayah.nama_wilayah}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ===== DATE FILTER ===== */}
          <div>
            <label className="form-label small">From</label>
            <input type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>

          <div>
            <label className="form-label small">To</label>
            <input type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <button className="btn btn-primary" onClick={handleFilter} disabled={loading}>
            Filter
          </button>

          <button className="btn btn-outline-secondary" onClick={handleReset} disabled={loading}>
            Reset
          </button>

          <button className="btn btn-success" onClick={handleExportExcel} disabled={exportLoading}>
            {exportLoading ? 'Downloading...' : 'Export Excel'}
          </button>
        </div>
      </div>

      {/* ===== KPI SUMMARY ===== */}
      {summary && (
        <>
          <div className="row g-4 mb-4">
            <div className="col-md-3">
              <div className="kpi-card">
                <small>Total Issue</small>
                <h2>{summary.total_issue}</h2>
                <div>
                  <span className="text-success fw-semibold">{summary.solved} Closed</span>
                  {' · '}
                  <span className="text-danger fw-semibold">{summary.unsolved} Open</span>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="kpi-card solving-card text-center">
                <small>Solving Ratio</small>
                <h2>{summary.solving_ratio_percent}%</h2>
              </div>
            </div>

            <div className="col-md-3">
              <div className="kpi-card">
                <small>Total Duration</small>
                <h2>{Number(summary.total_duration_minutes).toLocaleString()}</h2>
                <span className="text-muted">Minute</span>
              </div>
            </div>

            <div className="col-md-3">
              <div className="kpi-card text-center">
                <small>Average</small>
                <h2>{summary.avg_hour}</h2>
                <span className="text-muted">Hour</span>
              </div>
            </div>
          </div>

          {/* ===== CHANNEL SUMMARY ===== */}
          <div className="row g-4 mb-4">
            {channelSummary.map((channel, index) => (
              <div className="col-md-4 col-lg-3" key={index}>
                <div className="kpi-card channel-card">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-capitalize">{channel.channel_name}</strong>
                    <span className="badge bg-danger text-white">{channel.total_issue}</span>
                  </div>

                  <div className="small">
                    <span className="text-success fw-semibold">{channel.solved} Closed</span>
                    {' · '}
                    <span className="text-danger fw-semibold">{channel.unsolved} Open</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===== TABLE ===== */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0 fw-semibold">Detail Ticket</h5>
        </div>
        <div className="card-body">
          <DataTable
            columns={columns}
            data={data}
            progressPending={loading}
            pagination
            striped
            highlightOnHover
            responsive
          />
        </div>
      </div>
    </div>
  );
}

export default LaporanContactCenter;
